import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  ChangePasswordPayload,
  ConsignorProfile,
  UpdateProfilePayload,
} from '../types/consignorProfile';

// ─── Exports ──────────────────────────────────────────────────────────────────

export type ProfileResult = { ok: true; data: ConsignorProfile } | { ok: false; error: string };

/**
 * GET /api/consignor/profile
 *
 * Returns the current consignor's editable profile information.
 *
 * Response 200 JSON:
 * {
 *   "message": "OK",
 *   "data": {
 *     "name": "Full Name",
 *     "email": "user@example.com",
 *     "phone": "+52 33 1234 5678"   // nullable
 *   }
 * }
 *
 * Errors: 403, 500.
 */
export async function fetchConsignorProfile(): Promise<ProfileResult> {
  try {
    const { data } = await authClient.get<unknown>('/api/consignor/profile');
    const body = data as Record<string, unknown>;
    const r = (typeof body.data === 'object' && body.data ? body.data : body) as Record<
      string,
      unknown
    >;
    const phoneRaw = r.phone ?? r.phoneNumber ?? r.phone_number;
    return {
      ok: true,
      data: {
        name: String(r.name ?? r.fullName ?? r.full_name ?? '').trim(),
        email: String(r.email ?? '').trim(),
        phone: typeof phoneRaw === 'string' && phoneRaw.trim() ? phoneRaw.trim() : null,
      },
    };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar tu perfil.',
    };
  }
}

export type UpdateProfileResult = { ok: true } | { ok: false; error: string };

/**
 * PATCH /api/consignor/profile
 *
 * Partially updates the current consignor's profile. All fields are optional.
 *
 * Request body JSON (all optional):
 * {
 *   "name": "Full Name",
 *   "email": "user@example.com",
 *   "phone": "+52 33 1234 5678"   // pass null to clear
 * }
 *
 * Response 200: { "message": "OK" }
 * Errors:
 *   400 – validation error (e.g. invalid email format).
 *   403 – not authenticated.
 *   409 – email already in use by another account.
 *   500 – server error.
 */
export async function updateConsignorProfile(
  payload: UpdateProfilePayload
): Promise<UpdateProfileResult> {
  try {
    await authClient.patch('/api/consignor/profile', payload);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo actualizar tu perfil.',
    };
  }
}

export type ChangePasswordResult = { ok: true } | { ok: false; error: string };

/**
 * PATCH /api/consignor/profile/password
 *
 * Changes the current consignor's password.
 *
 * Request body JSON:
 * {
 *   "currentPassword": "old_password",
 *   "newPassword": "new_password"       // min 8 chars
 * }
 *
 * Response 200: { "message": "OK" }
 * Errors:
 *   400 – newPassword too short or missing fields.
 *   401 – currentPassword is incorrect.
 *   403 – not authenticated.
 *   500 – server error.
 */
export async function changeConsignorPassword(
  payload: ChangePasswordPayload
): Promise<ChangePasswordResult> {
  try {
    await authClient.patch('/api/consignor/profile/password', payload);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cambiar tu contraseña.',
    };
  }
}
